import React, { useCallback, useEffect, useRef, useState } from 'react';
import FlexBlock from '@/components/ui/FlexBlock/FlexBlock.tsx';
import Flyout, { FlyoutCloseButton } from '@/components/ui/Flyout/Flyout.tsx';
import { airTravelLogHistoryDemoColumns } from '@/constants/myClimate.ts';
import moment from 'moment/moment';
import Tooltip from '@/components/ui/Tooltip/Tooltip.tsx';
import LogForm from '@/pages/MyClimate/TripEmissionLog/LogForm.tsx';
import Spinner from '@/components/ui/Spinner/Spinner.tsx';
import { isEmpty, uniq, upperFirst } from 'lodash';
import Pagination from '@/components/ui/Pagination/Pagination.tsx';
import HistoryIcon from '@/assets/Icons/History-White.svg?react';
import EditIcon from '@/assets/Icons/Pen.svg?react';
import EyeIcon from '@/assets/Icons/Eye-Stroke.svg?react';
import { getTravelLogHistory } from '@/api/myClimate.ts';
import styles from './TripEmissionLogFlyout.module.scss';
import modalStyles from './TripEmissionLog.module.scss';
import { useQuery } from '@tanstack/react-query';
import queryKeys from '@/constants/queryKeys.ts';
import { formatDistance, formatWeight } from '@/utils/stringUtils.ts';
import classNames from 'classnames';
import TravelLogType = App.Enums.TravelLogType;
import TravelLogData = App.Data.TravelLogData;
import { trackEvent } from '@/utils/productAnalyticsUtil.ts';
import { useWhoamiStore } from '@/store/useWhoamiStore.ts';
import Modal from '@/components/ui/Modal/Modal.tsx';
import { useModalStore } from '@/store/useModalStore.ts';
import { productAnalyticsEvents } from '@/constants/productAnalyticsEvents.ts';

const travelerTooltipText = 'Traveler is the person who travels.';
const travelArrangerTooltipText = 'The travel arranger is the person who booked the trip.';
const tripLogHistoryDetailsModal = 'tripLogHistoryDetailsModal';

interface TripEmissionLogFlyoutProps {
	historyCount?: number;
	animateHistoryCount?: boolean;
	onHistoryChanged?: () => void | Promise<void>;
}

const TripEmissionLogFlyout: React.FC<TripEmissionLogFlyoutProps> = ({
	historyCount = 0,
	animateHistoryCount,
	onHistoryChanged,
}) => {
	const [travelLogHistoryPagination, setTravelLogHistoryPagination] = useState({ page: 1, perPage: 8 });
	const [selectedRowData, setSelectedRowData] = useState<TravelLogData | undefined>(undefined);
	const [selectedTripMode, setSelectedTripMode] = useState<TravelLogType | undefined>(undefined);
	const [editingMode, setEditingMode] = useState(false);
	const [viewMode, setViewMode] = useState(false);
	const hasOpenedHistoryRef = useRef(false);
	const currentUserId = useWhoamiStore(state => state.user?.id);
	const { openModal, closeModal } = useModalStore();

	const isSevenDaysAgo = useCallback((date: string) => {
		const givenTime = moment(date);
		const sevenDaysAgo = moment().subtract(7, 'days');
		return givenTime.isBefore(sevenDaysAgo);
	}, []);

	const handlePagination = (num: number) => {
		setTravelLogHistoryPagination({ page: num, perPage: 8 });
	};

	const handleActionClick = (action: 'edit' | 'view', rowId: number) => {
		const selectedRow = travelLogHistoryData?.data.find(item => item.id === rowId);
		if (!selectedRow) {
			return;
		}

		setSelectedRowData(selectedRow);
		setSelectedTripMode(selectedRow.type.toLowerCase() as TravelLogType);
		setEditingMode(action === 'edit');
		setViewMode(action === 'view');
		openModal(tripLogHistoryDetailsModal);
		trackEvent(productAnalyticsEvents.myClimate.tripLog.history.interact, {
			deduplicationKey: `${rowId}.${action}`,
		});
	};

	const formatTraveler = (travelLog: TravelLogData) => travelLog.travelerName || '–';
	const formatTravelArranger = (travelLog: TravelLogData) => travelLog.bookerName || '–';

	const canEditTravelLog = (travelLog: TravelLogData) => {
		if (!travelLog.createdAt || isSevenDaysAgo(travelLog.createdAt)) return false;

		if (!travelLog.bookerId && !travelLog.travelerId) return true;

		return travelLog.bookerId === currentUserId || travelLog.travelerId === currentUserId;
	};

	const {
		data: travelLogHistoryData,
		isLoading: isTravelLogHistoryDataLoading,
		isError: isTravelLogHistoryDataError,
		refetch: refetchTravelLogHistoryData,
	} = useQuery({
		queryKey: [queryKeys.travelLogHistory, travelLogHistoryPagination.page, travelLogHistoryPagination.perPage],
		queryFn: () => getTravelLogHistory(travelLogHistoryPagination),
		enabled: false,
	});

	const exitEditingMode = () => {
		closeModal(resetHistoryDetailsModal);
	};

	const resetHistoryDetailsModal = () => {
		setEditingMode(false);
		setViewMode(false);
		setSelectedTripMode(undefined);
		setSelectedRowData(undefined);
	};

	const handleHistoryOpen = () => {
		hasOpenedHistoryRef.current = true;
		void refetchTravelLogHistoryData();
		trackEvent(productAnalyticsEvents.myClimate.tripLog.history.open, {
			postHogEventName: 'trip_log_history_viewed',
			postHogProperties: { category: 'trip-log' },
		});
	};

	useEffect(() => {
		if (!hasOpenedHistoryRef.current) {
			return;
		}

		void refetchTravelLogHistoryData();
	}, [refetchTravelLogHistoryData, travelLogHistoryPagination.page]);

	useEffect(() => {
		const totalPages = travelLogHistoryData?.pagination.totalPages;

		if (totalPages && travelLogHistoryPagination.page > totalPages) {
			setTravelLogHistoryPagination(current => ({ ...current, page: totalPages }));
		}
	}, [travelLogHistoryData?.pagination.totalPages, travelLogHistoryPagination.page]);

	return (
		<Flyout
			id="emissionTargetHistoryFlyout"
			onOpen={handleHistoryOpen}
			button={
				<button type="button" className={styles.tripLogHistoryBttn}>
					<HistoryIcon /> Your trip logs
					{!!historyCount && (
						<span
							className={classNames(
								styles.historyCountBadge,
								animateHistoryCount && styles.historyCountBadgeAnimated,
							)}
						>
							{historyCount}
						</span>
					)}
				</button>
			}
			onClose={resetHistoryDetailsModal}
			className={styles.tripEmissionLogFlyout}
		>
			<FlexBlock justifyContent="space-between" alignItems="center" className={styles.flyoutHeader}>
				<p className={styles.flyoutTitle}>Your trip logs</p>
				<FlyoutCloseButton />
			</FlexBlock>
			<div className={styles.tableWrapper}>
				<table>
					<thead>
						<tr>
							{airTravelLogHistoryDemoColumns.map((column, index) => (
								<th key={index}>
									{column.accessorKey === 'traveler' ? (
										<FlexBlock alignItems="center" columnGap="2px" className={styles.filter}>
											Traveler{' '}
											<div className={styles.tooltip}>
												<Tooltip text={travelerTooltipText} />
											</div>
										</FlexBlock>
									) : column.accessorKey === 'travelArranger' ? (
										<FlexBlock alignItems="center" columnGap="2px" className={styles.filter}>
											Travel arranger{' '}
											<div className={styles.tooltip}>
												<Tooltip text={travelArrangerTooltipText} />
											</div>
										</FlexBlock>
									) : (
										column.header
									)}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{travelLogHistoryData &&
							travelLogHistoryData.data &&
							travelLogHistoryData.data.length > 0 &&
							travelLogHistoryData?.data?.map((travelLog, index) => {
								const flightClasses = uniq(
									travelLog?.flightLegs?.map(flightLeg => upperFirst(flightLeg.flightCabinClass)),
								);

								const typeClass = {
									flight: flightClasses.join(', '),
									accommodation: upperFirst(travelLog.accommodationType),
									mileage: upperFirst(travelLog.groundTransportationType),
								}[travelLog.type];

								const routeInfo = {
									flight: travelLog.flightLegs
										?.map(({ arrivalAirport, departureAirport }) =>
											[departureAirport.iataCode, arrivalAirport.iataCode].join('->'),
										)
										.join(', '),
									accommodation: travelLog.accommodationPlace?.name,
									mileage:
										travelLog.departurePlace && travelLog.arrivalPlace
											? `${travelLog.departurePlace?.addressLong} – ${travelLog.arrivalPlace?.addressLong}`
											: `${formatDistance(Number(travelLog.distance))}`,
								}[travelLog.type];
								const rowAction = canEditTravelLog(travelLog) ? 'edit' : 'view';
								const ActionIcon = rowAction === 'edit' ? EditIcon : EyeIcon;
								const travelLogId = travelLog.id;

								return (
									<tr key={travelLogId ?? index}>
										<td>{upperFirst(travelLog.type)}</td>
										<td>{routeInfo}</td>
										<td>{formatTravelArranger(travelLog)}</td>
										<td>{formatTraveler(travelLog)}</td>
										<td
											style={{ textWrap: 'nowrap' }}
										>{`${moment(travelLog.departureDate).format('DD.MM.YY')} – ${moment(travelLog.arrivalDate).format('DD.MM.YY')}`}</td>
										<td>{typeClass}</td>
										<td>
											{travelLog.type === 'accommodation'
												? '–'
												: travelLog.isRoundTrip
													? 'Yes'
													: 'No'}
										</td>
										<td style={{ textWrap: 'nowrap' }}>
											{travelLog.co2e ? formatWeight(travelLog.co2e) : '–'}
										</td>
										<td>
											{travelLog.createdAt ? moment(travelLog.createdAt).format('DD.MM.YY') : '–'}
										</td>
										<td>
											{travelLogId == null ? (
												'–'
											) : (
												<Tooltip
													icon={<ActionIcon />}
													text={
														rowAction === 'edit' ? 'Edit this travel log' : 'See full data'
													}
													triggerClassName={styles.actionBttn}
													onTriggerClick={() => handleActionClick(rowAction, travelLogId)}
												/>
											)}
										</td>
									</tr>
								);
							})}
					</tbody>
				</table>
				{isTravelLogHistoryDataLoading && (
					<div className={styles.spinnerWrapper}>
						<Spinner absolute />
					</div>
				)}
				{isTravelLogHistoryDataError && !isTravelLogHistoryDataLoading && !travelLogHistoryData && (
					<div className={styles.historyError} role="alert">
						<p>We couldn’t load your trip logs.</p>
						<button type="button" onClick={() => void refetchTravelLogHistoryData()}>
							Try again
						</button>
					</div>
				)}
				{travelLogHistoryData && !isTravelLogHistoryDataError && isEmpty(travelLogHistoryData.data) && (
					<p className={styles.emptyTable}>No data available</p>
				)}
			</div>
			{travelLogHistoryData && travelLogHistoryData?.pagination?.totalPages > 1 && (
				<Pagination
					records={travelLogHistoryData.pagination.total}
					current={travelLogHistoryData.pagination.currentPage}
					onChange={handlePagination}
					delta={2}
					fixed={1}
					limit={8}
				/>
			)}
			<Modal
				name={tripLogHistoryDetailsModal}
				className={modalStyles.dataComplianceLogModal}
				onClose={resetHistoryDetailsModal}
			>
				{selectedTripMode && selectedRowData && (
					<div className={modalStyles.modalContent}>
						<div className={modalStyles.modalHeader}>
							<h3>{editingMode ? 'Edit trip details' : 'View trip details'}</h3>
							<div className={modalStyles.modalTripData}>
								<span>{upperFirst(selectedRowData.type)}</span>
								<span>{formatTravelArranger(selectedRowData)}</span>
								<span>{formatTraveler(selectedRowData)}</span>
								<span>{`${moment(selectedRowData.departureDate).format('DD.MM.YY')} – ${moment(
									selectedRowData.arrivalDate,
								).format('DD.MM.YY')}`}</span>
							</div>
						</div>
						<LogForm
							compactDataCompliance
							tripMode={selectedTripMode}
							editingMode={editingMode}
							exitEditingMode={exitEditingMode}
							viewMode={viewMode}
							selectedRowData={selectedRowData}
							refetchTravelLogHistoryData={refetchTravelLogHistoryData}
							onTravelLogHistoryChanged={onHistoryChanged}
						/>
					</div>
				)}
			</Modal>
		</Flyout>
	);
};

export default TripEmissionLogFlyout;
